import {
    Component,
    EventEmitter,
    Input,
    NgModuleRef,
    OnInit,
    Output,
    TemplateRef,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    EvaluationInstance,
    EvaluationType,
} from '@masar/features/evaluate/types';
import { EvaluateService } from '@masar/features/evaluate/services/evaluate.service';
import { Subject } from 'rxjs';
import { ModalService, NotificationService } from 'mnm-webapp';
import { finalize, takeUntil } from 'rxjs/operators';
import { EvaluateItem } from '@masar/features/evaluate/interfaces';
import { EvaluationScoreBand, KpiResultPeriod } from '@masar/common/models';
import { EvaluationInstancePortalComponent } from '@masar/features/evaluate/components/evaluation-instance-portal/evaluation-instance-portal.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-evaluation-instance-list',
    templateUrl: './evaluation-instance-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class EvaluationInstanceListComponent implements OnInit {
    @Input() public title!: string;

    @Input() public savedEntityId?: string;

    @Input() public type!: EvaluationType;

    @Input() public item!: EvaluateItem;

    @Input() public canEdit = false;

    @Input() public customAction?: {
        name: string;
        template: TemplateRef<any>;
        item: KpiResultPeriod;
    };

    @Input() public useGroupedProjectStandards = false;

    @Input() public hideNotes = false;

    /** When true, show per-matrix score columns (e.g. project evaluations with two matrices). */
    @Input() public showMatrixScoreColumns = false;

    /** When true, hide Add once an instance exists (project allows one evaluation only). */
    @Input() public isSingleInstance = false;

    @Output() public update = new EventEmitter<void>();

    public instances: EvaluationInstance[] = [];

    public matrixScoreColumnLabels: string[] = [];

    public currentlyProcessing = new Set<string>();

    public isDetailDialogOpening = false;

    private scoreBandsByEvaluationId = new Map<string, EvaluationScoreBand[]>();

    private requestedScoreBandEvaluationIds = new Set<string>();

    public get canShowAddButton(): boolean {
        if (!this.canManageInstances) {
            return false;
        }

        return !this.isSingleInstance || this.instances.length === 0;
    }

    public get canManageInstances(): boolean {
        if (this.type === 'project') {
            return this.canEdit;
        }

        return this.item?.evaluateActionAbility?.canWrite ?? false;
    }

    public constructor(
        private readonly evaluateService: EvaluateService,
        private readonly modalService: ModalService,
        private readonly moduleRef: NgModuleRef<any>,
        private readonly notificationService: NotificationService,
        private readonly translateService: TranslateService
    ) {}

    public ngOnInit(): void {
        this.loadEvaluationMeta();
        this.reload();
    }

    public getMatrixRawScore(
        instance: EvaluationInstance,
        columnIndex: number
    ): number | null {
        if (columnIndex === 0) {
            return instance.firstMatrixRawScore ?? null;
        }

        if (columnIndex === 1) {
            return instance.secondMatrixRawScore ?? null;
        }

        return null;
    }

    public getScoreBand(
        instance: EvaluationInstance
    ): EvaluationScoreBand | null {
        const evaluationId = instance.evaluationId;
        const bands = evaluationId
            ? this.scoreBandsByEvaluationId.get(evaluationId)
            : undefined;

        if (!bands?.length) {
            return null;
        }

        return (
            bands.find(band => this.scoreMatchesBand(instance.score, band)) ??
            null
        );
    }

    public delete(item: EvaluationInstance): void {
        this.currentlyProcessing.add(item.id);
        this.evaluateService
            .deleteInstance(item.id, this.type)
            .pipe(finalize(() => this.currentlyProcessing.delete(item.id)))
            .subscribe(message => {
                this.notificationService.notifySuccess(message);
                this.update.emit();
                this.reload();
            });
    }

    public async showDetailDialog(
        id?: string,
        event?: Event,
        options?: { mode?: 'default' | 'view' }
    ): Promise<void> {
        event?.preventDefault();
        event?.stopPropagation();

        if (this.isDetailDialogOpening) {
            return;
        }

        this.isDetailDialogOpening = true;
        const subject = new Subject<void>();
        const behaviorFlags = this.resolveProjectOnlyBehaviorFlags();

        try {
            const component = await this.modalService.show(
                EvaluationInstancePortalComponent,
                {
                    title: this.title,
                    moduleRef: this.moduleRef,
                    beforeInit: c => {
                        c.type = this.type;
                        c.entityId = this.item.id;
                        c.itemId = id;
                        c.mode = options?.mode ?? this.resolveDialogMode(id);
                        c.savedEntityId = this.savedEntityId;
                        c.customAction = this.customAction;
                        c.useGroupedProjectStandards =
                            behaviorFlags.useGroupedProjectStandards;
                        c.hideNotes = behaviorFlags.hideNotes;
                    },

                    onDismiss: () => {
                        this.isDetailDialogOpening = false;
                        subject.next();
                        subject.complete();
                    },

                    size: {
                        width: '80%',
                    },
                }
            );

            component.update.pipe(takeUntil(subject)).subscribe(() => {
                this.isDetailDialogOpening = false;
                this.modalService.dismiss(component);
                this.update.next();
                this.reload();
            });
        } catch {
            this.isDetailDialogOpening = false;
            subject.complete();
        }
    }

    private reload(): void {
        this.evaluateService
            .listInstances(this.item.id, this.type)
            .subscribe(items => {
                this.instances = items;
                this.loadScoreBands(items);
            });
    }

    private loadScoreBands(instances: EvaluationInstance[]): void {
        const evaluationIds = new Set(
            instances
                .map(instance => instance.evaluationId)
                .filter((id): id is string => !!id)
        );

        evaluationIds.forEach(evaluationId =>
            this.ensureScoreBandsLoaded(evaluationId)
        );
    }

    private ensureScoreBandsLoaded(evaluationId: string): void {
        if (
            this.scoreBandsByEvaluationId.has(evaluationId) ||
            this.requestedScoreBandEvaluationIds.has(evaluationId)
        ) {
            return;
        }

        // Reserve before subscribing so a concurrent load cannot request the same bands twice.
        this.requestedScoreBandEvaluationIds.add(evaluationId);

        this.evaluateService.listScoreBands(evaluationId, this.type).subscribe({
            next: bands =>
                this.scoreBandsByEvaluationId.set(evaluationId, bands || []),
            error: () =>
                this.requestedScoreBandEvaluationIds.delete(evaluationId),
        });
    }

    private loadEvaluationMeta(): void {
        const fallbackLabels = [
            this.translateService.instant('translate_evaluation_matrix_1'),
            this.translateService.instant('translate_evaluation_matrix_2'),
        ];

        this.evaluateService
            .listEvaluations(this.type, this.savedEntityId)
            .subscribe(evaluations => {
                const evaluation =
                    (this.showMatrixScoreColumns
                        ? evaluations.find(e => e.matrixType === 'multi')
                        : null) ?? evaluations[0];

                if (!evaluation) {
                    if (this.showMatrixScoreColumns) {
                        this.matrixScoreColumnLabels = fallbackLabels;
                    }
                    return;
                }

                if (!this.showMatrixScoreColumns) {
                    this.ensureScoreBandsLoaded(evaluation.id);
                    return;
                }

                this.requestedScoreBandEvaluationIds.add(evaluation.id);

                this.evaluateService.getEvaluation(evaluation.id).subscribe({
                    next: fullEvaluation => {
                        this.scoreBandsByEvaluationId.set(
                            evaluation.id,
                            fullEvaluation.scoreBands || []
                        );

                        const matrices = fullEvaluation.matrices ?? [];
                        this.matrixScoreColumnLabels =
                            matrices.length > 0
                                ? matrices.map(
                                      matrix =>
                                          matrix.name ||
                                          matrix.nameAr ||
                                          matrix.nameEn ||
                                          ''
                                  )
                                : fallbackLabels;
                    },
                    error: () => {
                        this.requestedScoreBandEvaluationIds.delete(
                            evaluation.id
                        );
                        this.matrixScoreColumnLabels = fallbackLabels;
                    },
                });
            });
    }

    private scoreMatchesBand(
        score: number,
        band: EvaluationScoreBand
    ): boolean {
        const from = band.from;
        const to = band.to;

        return (
            (from == null && to != null && score < to) ||
            (from != null && to == null && from <= score) ||
            (from != null && to != null && from <= score && score < to)
        );
    }

    private resolveDialogMode(id?: string): 'default' | 'view' {
        if (!id && this.item?.evaluateActionAbility?.canWrite) {
            return 'default';
        }

        return this.canManageInstances ? 'default' : 'view';
    }

    private resolveProjectOnlyBehaviorFlags(): {
        useGroupedProjectStandards: boolean;
        hideNotes: boolean;
    } {
        const isProjectEvaluation = this.type === 'project';

        return {
            useGroupedProjectStandards:
                isProjectEvaluation && this.useGroupedProjectStandards,
            hideNotes: isProjectEvaluation && this.hideNotes,
        };
    }
}
